import {
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";

import {
  APIListValuesResponse,
  APISuccessResponse,
  genericValuesCallback,
} from "shared/api/api";
import { useDebounce } from "shared/hooks";
import { getAttributeLabel } from "shared/utils";

import Chip from "features/components/Chip";
import Dialog from "features/components/Dialog";
import TextField from "features/components/TextField";
import { FILTER_PASTE_DELIMITER } from "features/ui/Filters/constants";
import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";
import { SUPPORTED_DESCRIPTION_TABLE_FIELD_NAMES } from "features/ui/Filters/other/FilterDescriptionTable";
import { FilterOperator } from "features/ui/Filters/types";
import InfoIcon from "features/ui/Icons/Info";
import { Option, SelectOption } from "features/ui/Select";
import ValuesSearchList from "features/ui/ValuesSearchList";

import { FilterSkeleton } from "./components/FilterSkeleton";
import {
  DEBOUNCE_DELAY,
  DEFAULT_APPEND_ARBITRARY_OPTION,
  DEFAULT_DISABLE_ARBITRARY_TEXT,
  DEFAULT_LOAD_NUM_ITEMS,
  DEFAULT_WIDTH_PX,
  SELECTED_TAGS_TO_SHOW,
} from "./constants";
import { SelectFilterProps } from "./types";
import {
  createOnChangeHandler,
  getNewItemOption,
  getNoResultsMessage,
  getOptions,
  handleSelectAllAndCopyKeyDown,
  mapDistinctValuesToOptions,
  transformOptions,
} from "./utils";

const DEFAULT_FORMAT_FUNCTION = (options: SelectOption[]) => options;
const DEFAULT_LOAD_OPTIONS_ARGS = {};

const SelectFilter = ({
  fullWidth = true,
  fieldName,
  label,
  initialSelected = [],
  multiple = true,
  maxValues,
  search,
  loadDataOnOpen = false,
  loadOptionsFunc,
  loadOptionsArgs = DEFAULT_LOAD_OPTIONS_ARGS,
  onChange,
  onFilterChange,
  onInputChange,
  transformInitialSelectedFunc,
  formatLabelFunc = DEFAULT_FORMAT_FUNCTION,
  description,
  fieldNameForAPI,
  disableFiltering,
  enableMinMaxFilters,
  includeEmptyOption,
  staticFilters = [],
  filterType,
  filterDataType,
  customFilter,
  placeholder,
  defaultLoadLimit = DEFAULT_LOAD_NUM_ITEMS,
  disableArbitraryText = DEFAULT_DISABLE_ARBITRARY_TEXT,
  appendArbitraryOption = DEFAULT_APPEND_ARBITRARY_OPTION,
  disabled,
  testId,
  autoFocus,
  isInsideFilterBuilder,
  valuesEndpoint,
  limitTags: limitTagsProp,
  emptyOptionsMessage,
}: SelectFilterProps) => {
  const { t } = useTranslation("filters");
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!!autoFocus);

  const initialSelectedWithoutNull =
    initialSelected.length > 0 && initialSelected[0].id === "null"
      ? []
      : initialSelected;

  const [initialSelectedOption, setInitialSelectedOption] = useState(
    initialSelectedWithoutNull
  );
  const [initialized, setInitialized] = useState(
    transformInitialSelectedFunc === undefined || !initialSelected.length
  );

  const [input, setInput] = useState("");
  const debouncedInput = useDebounce(input, DEBOUNCE_DELAY);

  const updateInput = useCallback(
    (inputValue: string) => {
      setInput(inputValue);
      onInputChange && onInputChange(inputValue);
    },
    [onInputChange]
  );

  const showDescriptionsTable =
    SUPPORTED_DESCRIPTION_TABLE_FIELD_NAMES.includes(fieldName);

  const [openedOption, setOpenedOption] = useState<Option | null>(null);

  const filtersQuery = !disableFiltering
    ? getFiltersQuery(customFilter, staticFilters)
    : getFiltersQuery(customFilter);

  const loadOptionsData = useCallback(() => {
    if (loadOptionsFunc) {
      loadOptionsFunc({
        fieldName: fieldNameForAPI || fieldName,
        like: debouncedInput,
        filter: filtersQuery,
        limit: debouncedInput === "" ? defaultLoadLimit : undefined,
        ...loadOptionsArgs,
      })
        .then((options: SelectOption[]) => {
          setOptions(
            getOptions(
              t,
              formatLabelFunc(options),
              filterType,
              enableMinMaxFilters,
              includeEmptyOption
            )
          );
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (valuesEndpoint) {
      // fallback to the generic values endpoint
      genericValuesCallback(valuesEndpoint)({
        fieldName: fieldNameForAPI || fieldName,
        like: debouncedInput,
        limit: debouncedInput === "" ? defaultLoadLimit : undefined,
        filter: filtersQuery,
        ...loadOptionsArgs,
      })
        .then((data: APISuccessResponse<APIListValuesResponse>) => {
          const options = mapDistinctValuesToOptions(data.data?.distinctValues);
          setOptions(
            getOptions(
              t,
              formatLabelFunc(options),
              filterType,
              enableMinMaxFilters,
              includeEmptyOption
            )
          );
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    t,
    debouncedInput,
    defaultLoadLimit,
    enableMinMaxFilters,
    includeEmptyOption,
    fieldName,
    fieldNameForAPI,
    filterType,
    filtersQuery,
    formatLabelFunc,
    loadOptionsArgs,
    loadOptionsFunc,
    valuesEndpoint,
  ]);

  useEffect(() => {
    if (initialSelected.length > 0 && transformInitialSelectedFunc) {
      transformInitialSelectedFunc(initialSelected)
        .then((options: SelectOption[]) => {
          setInitialSelectedOption(formatLabelFunc(options));
        })
        .catch(() => {
          setInitialSelectedOption([]);
          onFilterChange &&
            onFilterChange({
              key: fieldName,
              op_id: FilterOperator.IN,
              values: [],
              dataType: filterDataType,
            });
        })
        .finally(() => {
          setInitialized(true);
        });
    }
    // handle scenario when values are removed
    else if (initialSelected?.length === 0) {
      setInitialSelectedOption([]);
    }

    setInitialSelectedOption(formatLabelFunc(initialSelected));
    // we do not want to fetch information every render of component so we
    // limit to only when elements change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, initialSelected.length]);

  useEffect(() => {
    // don't search on an empty query
    if (!debouncedInput && !loadDataOnOpen) {
      return;
    }

    setLoading(true);
    loadOptionsData();
  }, [debouncedInput, loadDataOnOpen, loadOptionsData]);

  const loadOptions = () => {
    // only load the options once
    if (options.length !== 0 || (search && !loadDataOnOpen)) {
      setOpen(true);

      return;
    }

    setLoading(true);

    loadOptionsData();
  };

  const handleOnChange = (values: SelectOption[]) => {
    // See https://blog.logrocket.com/using-strict-mode-react-18-guide-new-behaviors/:
    // - quote: React’s v18 strict mode does some interesting things regarding popular inbuilt hooks like useState, useMemo, and useReducer. Specifically, it invokes these functions twice in development and once (as expected) in production mode.
    const uniqueValues = Array.from(
      new Set(values.map(({ id }) => id.toString()))
    );

    onChange && onChange(uniqueValues);
    onFilterChange &&
      onFilterChange({
        key: fieldName,
        op_id: FilterOperator.IN,
        values: uniqueValues,
        dataType: filterDataType,
      });
  };

  const noResultsMessage = getNoResultsMessage(t, {
    loading,
    options,
    search,
    input,
    emptyOptionsMessage,
  });

  // this helps us set the initial value for single select (multiple = false)
  useEffect(() => {
    if (!multiple && initialSelectedOption?.length) {
      updateInput(initialSelectedOption[0].value.toString());
    }
  }, [multiple, initialSelectedOption, updateInput]);

  const selectedValues = useMemo(() => {
    if (!initialSelectedOption) return null;

    if (multiple) {
      return transformOptions(
        t,
        initialSelectedOption,
        filterType,
        enableMinMaxFilters,
        includeEmptyOption
      );
    } else if (initialSelectedOption.length > 0) {
      return initialSelectedOption[0];
    }

    return null;
  }, [
    t,
    multiple,
    initialSelectedOption,
    enableMinMaxFilters,
    includeEmptyOption,
    filterType,
  ]);

  if (!initialized) {
    return <FilterSkeleton />;
  }

  const onChangeHandler = createOnChangeHandler({
    handleOnChange,
    setInitialSelectedOption,
    maxValues,
  });

  const handleFilterOptions = (
    options: SelectOption[],
    state: FilterOptionsState<SelectOption>
  ) => {
    // if multiple=false && input equals currently selected value, don't filter anything, just show all options currently present
    if (
      !multiple &&
      selectedValues &&
      !Array.isArray(selectedValues) &&
      input === selectedValues.value
    ) {
      return options;
    }

    const filteredOptions = options.filter((option) => {
      if (option.value === null) {
        return false;
      }

      return option.value
        .toString()
        .toLowerCase()
        .includes(input.toLowerCase());
    });

    const { inputValue } = state;
    // Suggest the creation of a new value due to freeSolo prop
    const isExisting = options.some((option) => inputValue === option.value);
    if (
      appendArbitraryOption &&
      !disableArbitraryText &&
      inputValue !== "" &&
      !isExisting
    ) {
      filteredOptions.push(
        getNewItemOption(t, inputValue, options.length === 0)
      );
    }

    return filteredOptions;
  };

  const handleBlur = () => {
    // When arbitrary text is disabled, blurring must not commit the raw input
    // as a value/id (only real options are valid selections).
    if (disableArbitraryText || input.length === 0) return;

    const inputSelectOption: SelectOption = {
      id: input,
      value: input,
    };

    if (Array.isArray(selectedValues)) {
      onChangeHandler(null, [...selectedValues, inputSelectOption]);
      updateInput("");
    } else if (!selectedValues) {
      onChangeHandler(null, inputSelectOption);
      updateInput("");
    }
  };

  const onPaste = ({ target, clipboardData }: ClipboardEvent) => {
    // When arbitrary text is disabled, let pasted text filter the options
    // instead of committing it as raw values/ids.
    if (disableArbitraryText) return;

    const text = clipboardData.getData("Text");
    const splitValues = text.split(FILTER_PASTE_DELIMITER);
    const newValues = initialSelectedOption.concat(
      splitValues.filter(Boolean).map((value) => ({
        id: value.trim(),
        value: value.trim(),
      }))
    );

    // also unfocus the input so that message that asks the user to type something hides after paste
    (target as HTMLInputElement).blur();

    onChangeHandler(null, newValues);
  };

  const isMaxValueLimitReached = Boolean(
    maxValues &&
    selectedValues &&
    Array.isArray(selectedValues) &&
    selectedValues.length >= maxValues
  );

  const showDescriptionModal = (option: Option) => {
    if (option) {
      setOpenedOption(option);
    } else {
      setOpenedOption(null);
    }
  };

  const handleKeyDown = async (event: KeyboardEvent) => {
    const values: string[] = Array.isArray(selectedValues)
      ? selectedValues?.map((val) => val.id.toString()).filter(Boolean)
      : ([selectedValues?.id.toString()].filter(Boolean) as string[]);
    handleSelectAllAndCopyKeyDown(t, event, values);
  };

  const placeholderText = placeholder || t("select_or_paste_values");

  return (
    <>
      {openedOption && showDescriptionsTable && (
        <Dialog
          open={true}
          onClose={() => setOpenedOption(null)}
          maxWidth="sm"
          fullWidth={true}
        >
          <ValuesSearchList
            attribute={fieldName}
            title={getAttributeLabel(fieldName)}
            values={[openedOption.toString()]}
            key="values-modal"
            showDescriptionsTable={showDescriptionsTable}
          />
        </Dialog>
      )}
      <Autocomplete
        autoFocus={autoFocus}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        autoHighlight={true}
        disabled={disabled}
        id={`${fieldName}-autocomplete`}
        data-testid={testId || `${fieldName}-autocomplete`}
        options={options}
        limitTags={limitTagsProp ?? SELECTED_TAGS_TO_SHOW}
        value={selectedValues}
        onPaste={onPaste}
        open={open}
        onClose={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        freeSolo={!disableArbitraryText}
        disableClearable={selectedValues !== null}
        slotProps={{
          paper: {
            sx: {
              minWidth: "15rem",
            },
          },
          popper: {
            className: isInsideFilterBuilder ? "filterBuilder" : "",
          },
        }}
        isOptionEqualToValue={(current, selected) => {
          if (!current || !selected) return false;

          // selected can actually be an array of options or a single option depending on the multiple prop
          if (Array.isArray(selected)) {
            return selected.some(({ id: sId }) => sId === current.id);
          }

          return typeof selected !== "string" && current.id === selected.id;
        }}
        filterOptions={search ? handleFilterOptions : undefined}
        filterSelectedOptions={true}
        onOpen={loadOptions}
        loading={loading}
        getOptionLabel={(value) =>
          typeof value === "string"
            ? value
            : (value as SelectOption).value.toString()
        }
        getOptionKey={(option) =>
          typeof option === "string" ? option : option.id
        }
        noOptionsText={noResultsMessage}
        sx={fullWidth ? undefined : { width: DEFAULT_WIDTH_PX }}
        fullWidth={fullWidth}
        clearOnBlur={false}
        onChange={onChangeHandler}
        className="min-w-40"
        renderValue={
          multiple
            ? (value: any, getTagProps) =>
                value.map((option: SelectOption, index: number) => {
                  const { ...chipProps } = getTagProps({ index });

                  return (
                    <Chip
                      key={index}
                      label={option.label || option.value}
                      id={`${fieldName}-autocomplete-selected-${index}`}
                      data-testid={`${fieldName}-autocomplete-selected-${index}`}
                      {...chipProps}
                      className="autocomplete-selected-item !ml-1"
                      title={option.value.toString()}
                      onClick={(e) => {
                        e.stopPropagation();
                        showDescriptionModal(option.value.toString());
                      }}
                    />
                  );
                })
            : undefined
        }
        inputValue={search ? input : undefined}
        onInputChange={
          search
            ? (_, value, reason) => {
                // if search = true, this means we dont load data on open. We only load data when user types something! When clearing input to "" we should just keep the previous results shown until user types something
                // if search = false, this means we load data on open and it's always available after that - when clearing to "" ALL options should be available

                const selectAndDontClear = reason === "selectOption";

                if (search && (reason === "reset" || selectAndDontClear)) {
                  return;
                }

                updateInput(value);
              }
            : undefined
        }
        renderOption={(props, option) => {
          const { key, ...spanProps } = props;

          return (
            <span
              key={key}
              {...spanProps}
              title={option.value.toString()}
              data-testid={`option-${option.id}`}
              aria-disabled={isMaxValueLimitReached}
            >
              {option.label || option.value}
            </span>
          );
        }}
        renderInput={(params) => (
          <TextField
            autoFocus={autoFocus}
            {...params}
            label={label}
            placeholder={
              selectedValues &&
              Array.isArray(selectedValues) &&
              selectedValues.length > 0
                ? ""
                : placeholderText
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: description ? (
                  <>
                    <span className="inline-flex items-center mr-1 -mt-px">
                      <InfoIcon text={description} />
                    </span>
                    {params.slotProps.input.endAdornment}
                  </>
                ) : (
                  params.slotProps.input.endAdornment
                ),
              },
            }}
          />
        )}
      />
    </>
  );
};

export default SelectFilter;
